import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Heart, MessageCircle, Send, TrendingUp, TrendingDown, Loader2, ImagePlus, X } from "lucide-react";

const TAGS = ["Analysis", "Scalp", "Setup", "Macro News", "Psychology"];
const FILTERS = ["All", ...TAGS];

function initials(name) {
  return (name || "T").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
}
function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

const CommentBox = ({ post, onAdded, onRequireAuth }) => {
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!user) { onRequireAuth(); return; }
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/community/posts/${post.id}/comments`, { content: text });
      onAdded(post.id, data);
      setText("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSending(false); }
  };

  return (
    <div className="mt-3 pt-3 border-t border-white/5 space-y-3">
      {post.comments?.map((c) => (
        <div key={c.id} className="flex gap-2.5">
          <span className="h-7 w-7 shrink-0 rounded-full bg-white/8 text-slate-300 grid place-items-center font-data text-[10px]">{initials(c.author_name)}</span>
          <div className="flex-1 rounded-lg bg-[#0C1017] px-3 py-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-200">{c.author_name}</span>
              <span className="font-data text-[10px] text-slate-600">{timeAgo(c.created_at)}</span>
            </div>
            <p className="text-sm text-slate-300 mt-0.5">{c.content}</p>
          </div>
        </div>
      ))}
      <div className="flex gap-2">
        <Input
          data-testid="community-comment-input-field"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder="Add a comment…"
          className="bg-[#0C1017] border-white/10 h-9 text-sm text-white"
        />
        <Button data-testid="community-comment-submit-btn" onClick={submit} disabled={sending} size="sm" className="bg-white/8 hover:bg-brand text-white h-9 px-3">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
};

const PostCard = ({ post, onLike, onComment, onRequireAuth, index }) => {
  const [showComments, setShowComments] = useState(false);
  const sentimentStyle = post.sentiment === "Bullish"
    ? "text-gain border-gain/30 bg-gain/10"
    : post.sentiment === "Bearish" ? "text-loss border-loss/30 bg-loss/10" : "";

  return (
    <motion.div
      data-testid="community-post-card"
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: Math.min(index, 5) * 0.05 }}
      className="card-surface rounded-xl p-5"
    >
      <div className="flex items-center gap-3 mb-3">
        {post.author_avatar ? (
          <img src={post.author_avatar} alt="" className="h-10 w-10 rounded-full object-cover" />
        ) : (
          <span className="h-10 w-10 rounded-full bg-brand/15 text-brand grid place-items-center font-data text-sm font-semibold">{initials(post.author_name)}</span>
        )}
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-100">{post.author_name}</span>
            <span className="font-data text-[10px] text-slate-600">· {timeAgo(post.created_at)}</span>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="font-data text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-white/5 text-slate-400 border border-white/10">{post.tag}</span>
            {post.pair && <span className="font-data text-[10px] text-brand">{post.pair}</span>}
          </div>
        </div>
        {post.sentiment && (
          <span className={`inline-flex items-center gap-1 font-data text-[10px] uppercase tracking-widest px-2 py-1 rounded border ${sentimentStyle}`}>
            {post.sentiment === "Bullish" ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
            {post.sentiment}
          </span>
        )}
      </div>

      <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">{post.content}</p>
      {post.image_base64 && <img src={post.image_base64} alt="" className="mt-3 rounded-lg w-full object-cover max-h-72" />}

      <div className="flex items-center gap-4 mt-4">
        <button
          data-testid="community-post-like-button"
          onClick={() => onLike(post)}
          className={`inline-flex items-center gap-1.5 text-sm transition-colors ${post.liked_by_me ? "text-loss" : "text-slate-400 hover:text-loss"}`}
        >
          <Heart className={`h-4 w-4 ${post.liked_by_me ? "fill-loss" : ""}`} /> {post.likes_count}
        </button>
        <button
          data-testid="community-post-comment-button"
          onClick={() => setShowComments((s) => !s)}
          className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-brand transition-colors"
        >
          <MessageCircle className="h-4 w-4" /> {post.comments_count}
        </button>
      </div>

      {showComments && <CommentBox post={post} onAdded={onComment} onRequireAuth={onRequireAuth} />}
    </motion.div>
  );
};

export const CommunityPanel = ({ onRequireAuth }) => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [content, setContent] = useState("");
  const [pair, setPair] = useState("");
  const [tag, setTag] = useState("Analysis");
  const [sentiment, setSentiment] = useState("none");
  const [posting, setPosting] = useState(false);
  const [image, setImage] = useState(null);
  const imgRef = useRef(null);

  const onImage = (file) => {
    if (!file) return;
    if (!/image\/(png|jpe?g|webp)/.test(file.type)) { toast.error("Please attach a PNG, JPG or WEBP image"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const max = 1200;
        let { width, height } = img;
        if (width > max || height > max) {
          const s = max / Math.max(width, height);
          width = Math.round(width * s); height = Math.round(height * s);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        setImage(canvas.toDataURL("image/jpeg", 0.8));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const load = (f = filter) => {
    setLoading(true);
    api.get(`/community/posts${f !== "All" ? `?request_tag=${encodeURIComponent(f)}` : ""}`)
      .then(({ data }) => setPosts(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(filter); /* eslint-disable-next-line */ }, [filter]);

  const submitPost = async () => {
    if (!user) { onRequireAuth(); return; }
    if (!content.trim() && !image) { toast.error("Write something or attach a chart"); return; }
    setPosting(true);
    try {
      const { data } = await api.post("/community/posts", {
        content,
        pair: pair || null,
        tag,
        sentiment: sentiment === "none" ? null : sentiment,
        image_base64: image,
      });
      setPosts((p) => [data, ...p]);
      setContent(""); setPair(""); setSentiment("none"); setTag("Analysis"); setImage(null);
      toast.success("Posted to the floor");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setPosting(false); }
  };

  const like = async (post) => {
    if (!user) { onRequireAuth(); return; }
    setPosts((prev) => prev.map((p) => p.id === post.id
      ? { ...p, liked_by_me: !p.liked_by_me, likes_count: p.likes_count + (p.liked_by_me ? -1 : 1) } : p));
    try {
      const { data } = await api.post(`/community/posts/${post.id}/like`);
      setPosts((prev) => prev.map((p) => p.id === post.id ? { ...p, liked_by_me: data.liked_by_me, likes_count: data.likes_count } : p));
    } catch (e) {
      load();
    }
  };

  const addComment = (postId, comment) => {
    setPosts((prev) => prev.map((p) => p.id === postId
      ? { ...p, comments: [...(p.comments || []), comment], comments_count: p.comments_count + 1 } : p));
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* composer */}
          <div className="lg:col-span-4 lg:sticky lg:top-20">
            <div className="card-surface rounded-xl p-5">
              <h3 className="font-heading text-lg font-bold mb-4">Share a Setup</h3>
              <Textarea
                data-testid="community-create-post-textarea"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                onFocus={() => { if (!user) onRequireAuth(); }}
                placeholder={user ? "What's your read on the market?" : "Sign in to post your analysis…"}
                className="bg-[#0C1017] border-white/10 text-white min-h-[110px] resize-none"
              />
              <div className="grid grid-cols-2 gap-3 mt-3">
                <Input data-testid="community-post-pair-input" value={pair} onChange={(e) => setPair(e.target.value)} placeholder="Pair (EUR/USD)" className="bg-[#0C1017] border-white/10 h-10 font-data text-sm text-white" />
                <Select value={tag} onValueChange={setTag}>
                  <SelectTrigger data-testid="community-post-tag-select" className="bg-[#0C1017] border-white/10 h-10 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0C1017] border-white/10 text-white">
                    {TAGS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 mt-3" data-testid="community-post-sentiment-toggle">
                {[
                  { v: "Bullish", c: "text-gain border-gain/40 bg-gain/10", Icon: TrendingUp },
                  { v: "Bearish", c: "text-loss border-loss/40 bg-loss/10", Icon: TrendingDown },
                ].map((s) => (
                  <button
                    key={s.v}
                    onClick={() => setSentiment(sentiment === s.v ? "none" : s.v)}
                    className={`flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-md border font-data text-xs uppercase tracking-wider transition-colors ${sentiment === s.v ? s.c : "text-slate-400 border-white/10 hover:border-white/25"}`}
                  >
                    <s.Icon className="h-3.5 w-3.5" /> {s.v}
                  </button>
                ))}
              </div>
              {image ? (
                <div className="mt-3 relative rounded-lg overflow-hidden border border-white/10" data-testid="community-image-preview">
                  <img src={image} alt="attachment preview" className="w-full max-h-48 object-cover" />
                  <button
                    onClick={() => { setImage(null); if (imgRef.current) imgRef.current.value = ""; }}
                    data-testid="community-image-remove-btn"
                    className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/70 border border-white/20 grid place-items-center hover:bg-loss/80"
                  >
                    <X className="h-3.5 w-3.5 text-white" />
                  </button>
                </div>
              ) : (
                <button
                  data-testid="community-attach-image-btn"
                  onClick={() => { if (!user) { onRequireAuth(); return; } imgRef.current?.click(); }}
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 h-10 rounded-md border border-dashed border-white/15 hover:border-brand/50 text-slate-400 hover:text-brand font-data text-xs uppercase tracking-wider transition-colors"
                >
                  <ImagePlus className="h-4 w-4" /> Attach chart
                </button>
              )}
              <input ref={imgRef} data-testid="community-image-input" type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => onImage(e.target.files?.[0])} />
              <Button data-testid="community-submit-post-button" onClick={submitPost} disabled={posting} className="w-full mt-4 bg-brand hover:bg-brand-hover text-white font-semibold h-11 gap-2">
                {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {user ? "Post to Floor" : "Sign in to Post"}
              </Button>
            </div>
          </div>

          {/* feed */}
          <div className="lg:col-span-8">
            <div className="flex flex-wrap gap-2 mb-5">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  data-testid={`community-filter-${f.toLowerCase().replace(/ /g, "-")}`}
                  onClick={() => setFilter(f)}
                  className={`px-3.5 py-1.5 rounded-full font-data text-xs uppercase tracking-wider border transition-colors ${filter === f ? "bg-brand text-white border-brand" : "text-slate-400 border-white/10 hover:border-brand/40 hover:text-white"}`}
                >
                  {f}
                </button>
              ))}
            </div>

            <div data-testid="community-feed-container" className="space-y-4">
              {loading ? (
                <div className="py-24 grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>
              ) : posts.length === 0 ? (
                <div className="py-24 text-center text-slate-500 font-data text-sm">No posts yet. Be the first to share.</div>
              ) : (
                posts.map((p, i) => (
                  <PostCard key={p.id} post={p} index={i} onLike={like} onComment={addComment} onRequireAuth={onRequireAuth} />
                ))
              )}
            </div>
          </div>
        </div>
  );
};
